import { Box, Flex } from "@chakra-ui/react"
import * as React from "react"
import { FullScreen, useFullScreenHandle } from "react-full-screen"
import { useRecoilValue, useRecoilState, useSetRecoilState } from "recoil"
import {
  GAP_BETWEEN_OVERLAY_AND_BOTTOM_EDGE,
  HIDE_PLAYER_CONTROLLER_TIMEOUT,
  TRULIENCE_TEST_DEPLOY,
  USE_MOBILE_UI
} from "../../constants"
import { SDKContext } from "../../context/SDKContext"
import useLeaveOverlay from "../../hooks/useLeaveOverlay"
import { usePictureInPicture } from "../../hooks/usePictureInPicture"
import { activeSpeakerSelector } from "../../recoil/atoms/activeSpeaker"
import { pipParamsSelector } from "../../recoil/atoms/pictureInPicture"
import { brandingSelector } from "../../recoil/atoms/room"
import { screenShareUserSelector } from "../../recoil/atoms/screenShareUser"
import PiPBgBox from "../common/pipBgBox"
import MediaPlayer from "./_mediaPlayer"
import ScreenOverlay from "./_screenOverlay"
import { LogoOverlay } from "./logo-overlay"
import layoutStates from "../../recoil/atoms/layout"
import {
  watchPartyScreenSelector,
  watchPartyScreenLinkSelector
} from "../../recoil/atoms/watchPartyScreen"
import {
  auctionParamsSelector,
  auctionFinalResultAtom
} from "../../recoil/atoms/auction"
import { AuctionOverlayBid } from "../servicesPanel/auctions/auction-overlay-bid"
import { AuctionFinalOverlay } from "../servicesPanel/auctions/auction-final-overlay"
import { ToastConfigProps, useCustomToast } from "../../hooks/useToast"
import notifyUserIfPermissionChanged from "./notifyUserIfPermissionChanged"
import { showUserAvatar } from "../../util"
import {
  locationOfProductPageAtom,
  productOverlaySelector
} from "../../recoil/atoms/product-overlay"
import { ProductOverlay } from "../servicesPanel/badges/product-selector/overlay-product"
import { MediaController } from "./videoPlayer/media-controller"
import { mainWindowUserSelector } from "../../recoil/atoms/mainWindowUser"
import { playerPropsSelector } from "../../recoil/atoms/player"
import { FullScreenIcon } from "./buttons-main-screen/full-screen-icon"
import { EmotionalIcons } from "./buttons-main-screen/emotional-icons"
import { showRHSLeaveButtonSelector } from "../../recoil/selectors/layoutSelectors/layout-selectors"
import {
  whiteboardRoomDetailsAtom,
  whiteboardShareUserAtom
} from "../../recoil/atoms/whiteboard"
import { VRIcon } from "./buttons-main-screen/vr-icon"
import { PRODUCT_DESTINATION } from "../../constants/enums"
import { useCurrentAvatarInfo } from "src/conditionals/Avatar"

interface MainMediaScreenProps {
  widthMainScreen?: number | string
  heightMainScreen: number
  isAudience?: boolean
  isLeaveOverlayEnable?: boolean
  overlayWidth?: number | string
  isSmallMainScreen?: boolean
  isMediumAuctionBadge?: boolean
  maxWidthOverlay?: number
  styling?: React.CSSProperties
  showButtonsOnScreen?: boolean
  mainWindowUserProperties?: any
  localUserProperties?: any
  isWithoutTitle?: boolean
  isTransitionDesktop?: boolean
  isTablet: boolean
}

const UserType = window.MeetOnline.UserType

const _MainMediaScreen: React.FC<MainMediaScreenProps> = ({
  widthMainScreen,
  heightMainScreen,
  isAudience,
  isLeaveOverlayEnable,
  overlayWidth,
  isSmallMainScreen,
  maxWidthOverlay,
  styling,
  showButtonsOnScreen,
  isMediumAuctionBadge,
  mainWindowUserProperties,
  localUserProperties,
  isWithoutTitle,
  isTransitionDesktop,
  isTablet
}) => {
  const activeSpeaker = useRecoilValue(activeSpeakerSelector)
  // const isShareLinksModal = useRecoilValue(shareLinksModalAtom)
  const shareScreenUser = useRecoilValue(screenShareUserSelector)
  const isFullScreen = useFullScreenHandle()
  const { isModalBid } = useRecoilValue(auctionParamsSelector)
  const { isFinalModal } = useRecoilValue(auctionFinalResultAtom)
  const [recordingView] = useRecoilState(layoutStates.recordingView)
  const { isActivePiP, coverImage } = useRecoilValue(pipParamsSelector)
  const meetingClient = React.useContext(SDKContext)
  const { isOpen } = useRecoilValue(productOverlaySelector)
  const isWP = useRecoilValue(watchPartyScreenSelector)
  const roomStyles = useRecoilValue(brandingSelector)
  const showAvatar = roomStyles.enableDigitalAvatar
  const isWatchParty = isWP && !showAvatar
  const wpSrc = useRecoilValue(watchPartyScreenLinkSelector)
  const isFloatingDivActive = useRecoilValue(layoutStates.isFloatingDivActive)
  const mainWindowUser = useRecoilValue(mainWindowUserSelector)
  const setPlayerProps = useSetRecoilState(playerPropsSelector)
  const showLeaveButtonOnRHS = useRecoilValue(showRHSLeaveButtonSelector)
  const productPageLocation = useRecoilValue(locationOfProductPageAtom)
  const whiteboardRoomDetails = useRecoilValue(whiteboardRoomDetailsAtom)
  const whiteboardShareUser = useRecoilValue(whiteboardShareUserAtom)
  const { avatarId } = useCurrentAvatarInfo()

  const [supportsVR, setSupportsVR] = React.useState(false)

  React.useEffect(() => {
    async function checkVRSupport() {
      const supportsVR = await navigator.xr.isSessionSupported("immersive-vr")
      setSupportsVR(supportsVR)
    }
    checkVRSupport()
  }, [])

  const isAudioWatchParty = React.useMemo(
    () => (wpSrc && wpSrc.endsWith(".mp3") ? true : false),
    [wpSrc]
  )

  const { initStream, exitPictureInPicture, videoPiP, leavePictureinPicture } =
    usePictureInPicture()

  const playerBarActive = useRecoilValue(layoutStates.playerBarActive)

  const { showLeaveOverlay, setShowLeaveOverlay } = useLeaveOverlay(true, false)
  let mainWindowUserUid: string = ""
  mainWindowUserUid = mainWindowUserProperties.mainWindowUserId

  let enableNBAOverlay = false

  if (shareScreenUser && shareScreenUser.uid) {
    // mainWindowUserUid = shareScreenUser.uid
  } else {
    if (mainWindowUserUid === "modNotPresentVideo") {
      mainWindowUserUid = ""
    }
    if (mainWindowUserUid !== "") {
      // if (localUserProperties?.type !== UserType?.AUDIENCE && allUserList.length > 1 && (userIdsSmallWindows.list).length == 1 && (userIdsSmallWindows.list).indexOf(_localUserId) > -1){
      if (
        localUserProperties?.type !== UserType?.AUDIENCE &&
        // allUserList.length > 1 &&
        mainWindowUserUid === localUserProperties?.id &&
        (localUserProperties?.camEnabled === true ||
          localUserProperties?.micEnabled === true)
      ) {
        // if only one publisher in room and all other are audience members
        enableNBAOverlay = true
      } else {
      }
    } else {
      // mainWindowUserUid = activeSpeaker?.uid
    }
  }

  // Toast messages on Permission change for broadcaster/audience by moderator - START

  const config: ToastConfigProps = {
    toastOptions: {
      containerStyle: {
        margin: "0.1rem"
      }
    },
    titleStyles: {
      fontWeight: 700
    },
    position: isTablet ? "top" : "bottom-right"
  }
  const showToastMessage = useCustomToast(config)

  React.useEffect(() => {
    notifyUserIfPermissionChanged(
      localUserProperties.permissions,
      localUserProperties.oldPermissions,
      showToastMessage
    )
  }, [
    localUserProperties.permissions,
    localUserProperties.oldPermissions,
    showToastMessage
  ])

  const mainMediaScreenHeight = React.useMemo(() => {
    return isFullScreen && isFullScreen?.active ? "100vh" : heightMainScreen
  }, [heightMainScreen, isFullScreen])

  const borderRadius = !recordingView && "0.375rem"

  const _stylingWithBorder: React.CSSProperties = React.useMemo(() => {
    return {
      backgroundColor: roomStyles?.alertBackgroundColor ?? "black",
      height: isFloatingDivActive ? "100%" : mainMediaScreenHeight,
      objectFit: "cover",
      borderRadius: borderRadius,
      maxHeight: "100%",
      border: "2px solid",
      maxWidth: !isFullScreen.active && !isFloatingDivActive && widthMainScreen,
      borderColor: roomStyles.highlighterColor ?? "#000000"
    }
  }, [
    borderRadius,
    isFloatingDivActive,
    isFullScreen.active,
    mainMediaScreenHeight,
    roomStyles?.alertBackgroundColor,
    roomStyles?.highlighterColor,
    widthMainScreen
  ])

  const _stylingWithoutBorder: React.CSSProperties = React.useMemo(() => {
    return {
      backgroundColor: roomStyles?.alertBackgroundColor ?? "black",
      height: isFloatingDivActive ? "100%" : mainMediaScreenHeight,
      objectFit: "cover",
      borderRadius: borderRadius,
      maxWidth: !isFullScreen.active && !isFloatingDivActive && widthMainScreen,
      maxHeight: "100%"
    }
  }, [
    isFloatingDivActive,
    isFullScreen.active,
    mainMediaScreenHeight,
    borderRadius,
    roomStyles?.alertBackgroundColor,
    widthMainScreen
  ])

  const mainMediaScreenContainerStyles: React.CSSProperties =
    React.useMemo(() => {
      return {
        maxWidth:
          !isFullScreen?.active && !isFloatingDivActive && widthMainScreen,
        margin: "0 auto"
      }
    }, [isFullScreen, widthMainScreen, isFloatingDivActive])

  const isLogo = React.useMemo(() => {
    if (whiteboardShareUser != null) return false

    if (roomStyles?.showLogo && roomStyles.logo && !isOpen) {
      return true
    }
    return false
  }, [isOpen, roomStyles.logo, roomStyles?.showLogo, whiteboardShareUser])

  React.useEffect(() => {
    if (videoPiP) {
      videoPiP.addEventListener("leavepictureinpicture", leavePictureinPicture)
      return () => {
        videoPiP.removeEventListener(
          "leavepictureinpicture",
          leavePictureinPicture
        )
      }
    }
  }, [leavePictureinPicture, videoPiP])

  React.useEffect(() => {
    if (
      !recordingView &&
      !isWatchParty &&
      !(isWatchParty && isAudioWatchParty)
    ) {
      if (mainWindowUserUid) {
        const mainMediaStreamTrack: MediaStreamTrack =
          meetingClient.getMediaTracks(mainWindowUserUid).videoTrack
            ?._mediaStreamTrack

        if (!mainWindowUserProperties?.camEnabled) {
          exitPictureInPicture()
        }

        if (mainMediaStreamTrack !== undefined) {
          initStream(mainMediaStreamTrack)
        }
      }
    }
  }, [
    initStream,
    exitPictureInPicture,
    mainWindowUserProperties?.camEnabled,
    recordingView,
    meetingClient,
    mainWindowUserUid,
    isWatchParty,
    isAudioWatchParty
  ])

  const handleShowFullScreen = React.useCallback(
    () =>
      isFullScreen && isFullScreen?.active
        ? isFullScreen.exit()
        : isFullScreen.enter(),
    [isFullScreen]
  )

  const hideUserAvatar = !showUserAvatar({
    isWatchParty,
    isAudioWatchParty,
    userId: mainWindowUserUid,
    isCamEnabled: mainWindowUserProperties?.camEnabled
  })

  const isBorder = React.useMemo(() => {
    return (
      mainWindowUserUid === "ss" + activeSpeaker.uid ||
      mainWindowUserUid === activeSpeaker.uid
      /*  && allUserList.length > 2 */
    )
  }, [activeSpeaker.uid, mainWindowUserUid])

  const borderStyling = React.useMemo(() => {
    return isBorder ? _stylingWithBorder : _stylingWithoutBorder
  }, [isBorder, _stylingWithBorder, _stylingWithoutBorder])

  const productSelectorSizeOverlayProps = React.useMemo(() => {
    return {
      height: `calc(${
        heightMainScreen - GAP_BETWEEN_OVERLAY_AND_BOTTOM_EDGE * 2
      }px - 1rem)`,
      width: `calc(${widthMainScreen}px - 80px)`,
      isLargeMenu:
        isWithoutTitle !== undefined
          ? !isWithoutTitle
          : +widthMainScreen - 80 > 700,
      isSmallMenu: +widthMainScreen - 80 < 450
    }
  }, [heightMainScreen, isWithoutTitle, widthMainScreen])

  // This function shows player controller when mouse enter
  const handleMouseEnter = () => {
    setPlayerProps({ isOpenControlDesktop: true })
  }

  // This function hide player controller when mouse leave
  const handleMouseLeave = () => {
    setPlayerProps({ isOpenControlDesktop: false })
  }

  // This effect hide the player controller after 4000 sec
  React.useEffect(() => {
    if (!isWatchParty) {
      let timer = setTimeout(() => {
        setPlayerProps({
          isOpenControlDesktop: false,
          isEnabledAutoPlay: true
        })
      }, HIDE_PLAYER_CONTROLLER_TIMEOUT)
      return () => clearTimeout(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWatchParty])

  const isLivesellIframe =
    productPageLocation === PRODUCT_DESTINATION.LIVESELL_IFRAME

  // TODO: mainWindowUserUid should has avatarId, if avatar is shown on MVW
  const mainMediaUserId = avatarId ?? mainWindowUserUid

  return (
    <FullScreen className="mainScreenOuter" handle={isFullScreen}>
      <Box style={styling} height="100%">
        <Box
          style={mainMediaScreenContainerStyles}
          h={"100%"}
          position="relative"
          overflow="hidden"
          borderRadius={borderRadius}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {isModalBid && isFullScreen && isFullScreen?.active && (
            <AuctionOverlayBid isMobile={false} />
          )}
          {isFinalModal && isFullScreen && isFullScreen?.active && (
            <AuctionFinalOverlay />
          )}

          {isActivePiP /* && !isAudioWatchParty */ &&
            document.pictureInPictureEnabled && (
              // mainWindowUserProperties?.camEnabled &&
              <PiPBgBox coverImage={coverImage} isActiveScreen={isBorder} />
            )}

          {/* CM(LIVESELL) */}
          {/* {(!isOpen || !isFloatingDivActive) && !isLivesellIframe && (
            <Flex pos="absolute" right="0.75rem" top="0.5rem" zIndex="100000">
              <FullScreenIcon
                handleShowsFullScreen={handleShowFullScreen}
                isFullScreen={isFullScreen && isFullScreen?.active}
              />
            </Flex>
          )} */}
          {/* CM(LEARNMACHINE) */}
          <Flex
            pos="absolute"
            right="0.75rem"
            top="0.5rem"
            zIndex="100000"
            gap="10px"
          >
            {supportsVR && <VRIcon></VRIcon>}
            {!isOpen &&
              !isFloatingDivActive &&
              !whiteboardShareUser &&
              !wpSrc && (
                <FullScreenIcon
                  handleShowsFullScreen={handleShowFullScreen}
                  isFullScreen={isFullScreen && isFullScreen?.active}
                />
              )}
          </Flex>

          {isLogo && <LogoOverlay src={roomStyles?.logo} />}
          {
            <MediaPlayer
              key={`player-${mainMediaUserId}`}
              uid={mainWindowUserUid}
              styling={borderStyling}
              setShowLeaveOverlay={isLeaveOverlayEnable && setShowLeaveOverlay}
              showLeaveOverlay={isLeaveOverlayEnable && showLeaveOverlay}
              showLeave={isLeaveOverlayEnable}
              showNBAOverlay={enableNBAOverlay}
              isSmallWindow={false}
              isTouchDelay={isLeaveOverlayEnable}
              hideAvatar={hideUserAvatar}
              localUserProperties={
                localUserProperties.localUserId === mainWindowUserUid
                  ? localUserProperties
                  : {}
              }
              index={0}
              isFullScreen={isFullScreen?.active}
              isTablet={isTablet}
              isMainMobileScreen={isTablet}
              handleShowFullscreen={handleShowFullScreen}
              whiteboardRoomDetails={whiteboardRoomDetails}
              whiteboardShareUser={whiteboardShareUser}
            />
          }

          {!isWatchParty && !isFloatingDivActive && !whiteboardShareUser && (
            <Box
              pos="absolute"
              bottom={playerBarActive ? "71px" : "16px"} // 55px height of player controller  + 16px bottom padding
              // left={"10px"}
              zIndex={1101}
              onClick={() => setShowLeaveOverlay(!showLeaveOverlay)}
            >
              <ScreenOverlay
                productSelectorSizeOverlayProps={
                  productSelectorSizeOverlayProps
                }
                isTablet={isTablet}
                width={overlayWidth}
                isSmallMainScreen={isSmallMainScreen}
                maxWidthOverlay={maxWidthOverlay}
                isBorder={isBorder}
                maxWidthChatOverlay={maxWidthOverlay}
                isMediumAuctionBadge={isMediumAuctionBadge}
              />
            </Box>
          )}
          {!isFloatingDivActive && (
            <div
              style={{
                position: "absolute",
                bottom: isFullScreen?.active
                  ? "50%"
                  : isTablet
                  ? "0px"
                  : "16px",
                transform: isFullScreen?.active && "translate(0%, 50%)",

                left: "2px",

                zIndex: 100000000
              }}
            >
              <ProductOverlay
                width={+widthMainScreen}
                height={heightMainScreen}
                isWithoutTitle={isWithoutTitle}
                isFullScreen={isFullScreen.active}
              />
            </div>
          )}
        </Box>
      </Box>
    </FullScreen>
  )
}

export const MainMediaScreen = React.memo(_MainMediaScreen)
